import { useEffect } from "react";

/**
 * Lightweight SEO helper — updates <title>, meta description, canonical,
 * OG/Twitter tags, and (optionally) injects a JSON-LD block for the current route.
 *
 * No react-helmet dependency — direct DOM writes inside useEffect.
 */
export default function SEO({
  title,
  description,
  canonical,
  ogImage,
  jsonLd,
}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const setMeta = (selector, attrKey, attrVal, content) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrKey, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      return el;
    };

    const created = [];

    if (description) {
      setMeta('meta[name="description"]', "name", "description", description);
      setMeta('meta[property="og:description"]', "property", "og:description", description);
      setMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    }
    if (title) {
      setMeta('meta[property="og:title"]', "property", "og:title", title);
      setMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    }
    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
        created.push(link);
      }
      link.setAttribute("href", canonical);
      setMeta('meta[property="og:url"]', "property", "og:url", canonical);
    }
    if (ogImage) {
      setMeta('meta[property="og:image"]', "property", "og:image", ogImage);
      setMeta('meta[name="twitter:image"]', "name", "twitter:image", ogImage);
    }

    let ldScript = null;
    if (jsonLd) {
      ldScript = document.createElement("script");
      ldScript.type = "application/ld+json";
      ldScript.setAttribute("data-seo-jsonld", "1");
      ldScript.text = JSON.stringify(jsonLd);
      document.head.appendChild(ldScript);
    }

    return () => {
      document.title = prevTitle;
      if (ldScript && ldScript.parentNode) ldScript.parentNode.removeChild(ldScript);
      created.forEach((n) => n.parentNode && n.parentNode.removeChild(n));
    };
  }, [title, description, canonical, ogImage, jsonLd]);

  return null;
}
